/**
 * The signed-in session lives in two first-party cookies the app sets itself. They can't be
 * HttpOnly: the API is on another domain, so only this page's script can write them.
 */
export const ACCESS_TOKEN_COOKIE = "sdp_access_token";
export const REFRESH_TOKEN_COOKIE = "sdp_refresh_token";

/** Cognito's default refresh token lifetime. */
export const REFRESH_TOKEN_MAX_AGE_SEC = 30 * 24 * 60 * 60;

function isHttps(): boolean {
  return window.location.protocol === "https:";
}

/** The Set-Cookie style string for one of our cookies. */
export function buildCookie(name: string, value: string, maxAgeSec: number, secure: boolean): string {
  const parts = [`${name}=${encodeURIComponent(value)}`, "Path=/", `Max-Age=${maxAgeSec}`, "SameSite=Lax"];
  if (secure) parts.push("Secure");
  return parts.join("; ");
}

function writeCookie(name: string, value: string, maxAgeSec: number) {
  document.cookie = buildCookie(name, value, maxAgeSec, isHttps());
}

export function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  const match = document.cookie.split("; ").find((c) => c.startsWith(prefix));
  return match ? decodeURIComponent(match.slice(prefix.length)) : null;
}

/** Seconds since the epoch at which a JWT expires, or null if it can't be read. */
export function getTokenExpiry(token: string): number | null {
  try {
    const payload = token.split(".")[1];
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const exp = JSON.parse(json).exp;
    return typeof exp === "number" ? exp : null;
  } catch {
    return null;
  }
}

/** Store the access token until it expires (falls back to `expiresInSec` if the token has no exp). */
export function setAccessToken(token: string, expiresInSec = 3600) {
  const exp = getTokenExpiry(token);
  const maxAge = exp !== null ? Math.max(0, Math.floor(exp - Date.now() / 1000)) : expiresInSec;
  writeCookie(ACCESS_TOKEN_COOKIE, token, maxAge);
}

export function setRefreshToken(token: string) {
  writeCookie(REFRESH_TOKEN_COOKIE, token, REFRESH_TOKEN_MAX_AGE_SEC);
}

export function getAccessToken(): string | null {
  return readCookie(ACCESS_TOKEN_COOKIE);
}

export function getRefreshToken(): string | null {
  return readCookie(REFRESH_TOKEN_COOKIE);
}

export function clearAuthCookies() {
  writeCookie(ACCESS_TOKEN_COOKIE, "", 0);
  writeCookie(REFRESH_TOKEN_COOKIE, "", 0);
}
